<template>
  <div class="article-page">
    <!-- 面包屑 -->
    <nav class="breadcrumb-nav">
      <router-link to="/">首页</router-link>
      <span class="separator">/</span>
      <router-link to="/research">研究中心</router-link>
      <span class="separator">/</span>
      <router-link to="/research#trends">审计风控趋势</router-link>
      <span class="separator">/</span>
      <span class="current">持续审计：从理论到实践的全景图</span>
    </nav>

    <article class="container">
      <!-- 头部 -->
      <header class="article-header">
        <div class="article-tags">
          <span class="tag">审计风控趋势</span>
          <span class="tag">持续审计</span>
          <span class="tag">案例研究</span>
        </div>
        <h1 class="article-title">持续审计：从理论到实践的全景图</h1>
        <p class="article-subtitle">技术架构 · 实施路径 · 组织体系 · 覆盖率从15%到85%的转型实践</p>
        <div class="article-meta">
          <span><i class="far fa-calendar-alt"></i> 2026-01-28</span>
          <span><i class="far fa-clock"></i> 阅读约 18 分钟</span>
          <span><i class="far fa-user"></i> 古杉研究中心</span>
        </div>
      </header>

      <!-- 导读 -->
      <section class="intro-card">
        <div class="intro-icon">⚡</div>
        <div>
          <h3>导读</h3>
          <p>持续审计（Continuous Auditing，CA）是当前企业内部审计转型最重要的方向之一。本文系统讲解持续审计的概念框架、技术架构与实施路径，并通过某国有大型商业银行、某头部能源集团两个真实落地案例，展示企业如何完成从传统年度审计向持续审计的蜕变——审计覆盖率从15%跃升至85%。</p>
        </div>
      </section>

      <!-- 目录 -->
      <nav class="toc">
        <h3>📑 目录</h3>
        <ul>
          <li><a href="#section1">一、什么是持续审计</a></li>
          <li><a href="#section2">二、持续审计的技术架构</a></li>
          <li><a href="#section3">三、实施路径：四阶段方法论</a></li>
          <li><a href="#section4">四、组织体系建设</a></li>
          <li><a href="#section5">五、案例一：某国有大型商业银行</a></li>
          <li><a href="#section6">六、案例二：某头部能源集团</a></li>
          <li><a href="#section7">七、常见挑战与应对策略</a></li>
        </ul>
      </nav>

      <!-- 正文 -->
      <div class="article-body">

        <!-- 第一章 -->
        <section id="section1" class="chapter">
          <h2>一、什么是持续审计</h2>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">📌</div>
              <div>
                <h4>定义</h4>
                <p>持续审计是一种自动化审计方法，利用技术手段对交易、控制和风险进行近实时或高频率的系统化监控与评价，打破传统审计"年度一次"的时间局限，实现审计从"事后核查"向"事中监控"的根本转变。</p>
              </div>
            </div>
          </div>

          <h3>传统审计 vs 持续审计</h3>
          <div class="compare-table-wrap">
            <table class="compare-table">
              <thead>
                <tr>
                  <th>对比维度</th>
                  <th>传统年度审计</th>
                  <th>持续审计</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in compareRows" :key="row.dim">
                  <td class="dim-col">{{ row.dim }}</td>
                  <td class="old-col">{{ row.old }}</td>
                  <td class="new-col">{{ row.newVal }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-number">85%</div>
              <div class="stat-label">持续审计可实现的平均审计覆盖率（传统仅15%）</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">70%</div>
              <div class="stat-label">问题发现时效提升，从数月缩短至数天</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">40%</div>
              <div class="stat-label">审计人力成本节省（自动化替代重复性核查）</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">3年</div>
              <div class="stat-label">典型持续审计体系从0到成熟的建设周期</div>
            </div>
          </div>
        </section>

        <!-- 第二章 -->
        <section id="section2" class="chapter">
          <h2>二、持续审计的技术架构</h2>
          <p>持续审计的底层能力来自三层技术架构：数据采集层、分析引擎层和审计管理层，三层协同共同支撑高频审计能力。</p>

          <div class="arch-layers">
            <div class="arch-layer" v-for="layer in archLayers" :key="layer.name" :style="{ background: layer.bg }">
              <div class="arch-layer-title" :style="{ color: layer.titleColor }">
                <span class="arch-icon">{{ layer.icon }}</span>
                {{ layer.name }}
              </div>
              <div class="arch-layer-items">
                <div class="arch-item" v-for="item in layer.items" :key="item.name">
                  <div class="arch-item-name">{{ item.name }}</div>
                  <div class="arch-item-desc">{{ item.desc }}</div>
                </div>
              </div>
            </div>
          </div>

          <h3>关键技术能力要求</h3>
          <div class="tech-grid">
            <div class="tech-card" v-for="t in techCapabilities" :key="t.name">
              <div class="tech-icon">{{ t.icon }}</div>
              <h4>{{ t.name }}</h4>
              <p>{{ t.desc }}</p>
              <div class="tech-tools">
                <span v-for="tool in t.tools" :key="tool">{{ tool }}</span>
              </div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">💡</div>
            <div>
              <h4>数据底座是先决条件</h4>
              <p>持续审计的成败70%取决于数据基础。没有覆盖核心业务系统的数据采集能力、没有可信的数据质量，持续审计就是"无源之水"。建议企业先完成数据治理底座建设，再推进持续审计落地。</p>
            </div>
          </div>
        </section>

        <!-- 第三章 -->
        <section id="section3" class="chapter">
          <h2>三、实施路径：四阶段方法论</h2>

          <div class="impl-phases">
            <div class="impl-phase" v-for="(phase, i) in implPhases" :key="i">
              <div class="impl-phase-header" :style="{ background: phase.color }">
                <span class="phase-num">阶段 {{ i + 1 }}</span>
                <span class="phase-title">{{ phase.title }}</span>
                <span class="phase-duration">{{ phase.duration }}</span>
              </div>
              <div class="impl-phase-body">
                <p class="phase-goal"><strong>目标：</strong>{{ phase.goal }}</p>
                <div class="phase-tasks">
                  <div class="phase-task" v-for="(task, j) in phase.tasks" :key="j">
                    <span class="task-num">{{ j + 1 }}</span>
                    <div>
                      <div class="task-name">{{ task.name }}</div>
                      <div class="task-desc">{{ task.desc }}</div>
                    </div>
                  </div>
                </div>
                <div class="phase-deliverable">📦 交付物：{{ phase.deliverable }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第四章 -->
        <section id="section4" class="chapter">
          <h2>四、组织体系建设</h2>
          <p>技术只是工具，持续审计的持久运转依赖完善的组织机制。需在人员、流程、制度三个维度同步建设。</p>

          <h3>4.1 持续审计组织角色</h3>
          <div class="org-roles">
            <div class="org-role" v-for="role in orgRoles" :key="role.title">
              <div class="role-badge" :style="{ background: role.color }">{{ role.title }}</div>
              <div class="role-info">
                <p class="role-resp"><strong>职责：</strong>{{ role.resp }}</p>
                <p class="role-skill"><strong>能力要求：</strong>{{ role.skill }}</p>
              </div>
            </div>
          </div>

          <h3>4.2 持续审计运营机制</h3>
          <div class="ops-cycle">
            <div class="ops-step" v-for="(step, i) in opsCycle" :key="i">
              <div class="ops-marker" :style="{ background: step.color }">{{ step.icon }}</div>
              <h4>{{ step.title }}</h4>
              <p>{{ step.desc }}</p>
              <div class="ops-freq">频率：{{ step.freq }}</div>
            </div>
          </div>

          <h3>4.3 成功关键因素（KSF）</h3>
          <div class="ksf-grid">
            <div class="ksf-card" v-for="ksf in ksfList" :key="ksf.factor">
              <div class="ksf-icon">{{ ksf.icon }}</div>
              <h4>{{ ksf.factor }}</h4>
              <p>{{ ksf.desc }}</p>
            </div>
          </div>
        </section>

        <!-- 第五章 -->
        <section id="section5" class="chapter">
          <h2>五、案例一：某国有大型商业银行</h2>

          <div class="case-banner" style="background: linear-gradient(135deg, #0E1F52, #1F3F8F);">
            <div class="case-banner-info">
              <div class="case-banner-tag">金融行业 · 国有大行</div>
              <h3>从年度抽查到持续智能监控</h3>
              <p>审计覆盖率从 <strong>15%</strong> 跃升至 <strong>78%</strong>，问题发现周期从 <strong>数月</strong> 压缩至 <strong>72小时</strong></p>
            </div>
          </div>

          <div class="case-section">
            <h3>5.1 背景与挑战</h3>
            <div class="challenge-list">
              <div class="challenge-item" v-for="(c, i) in bankChallenges" :key="i">
                <div class="challenge-marker">{{ i + 1 }}</div>
                <div class="challenge-content">
                  <h4>{{ c.title }}</h4>
                  <p>{{ c.desc }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="case-section">
            <h3>5.2 解决方案</h3>
            <div class="solution-steps">
              <div class="solution-step" v-for="(s, i) in bankSolutions" :key="i" :style="{ borderLeftColor: s.color }">
                <div class="solution-step-title">{{ s.title }}</div>
                <p>{{ s.desc }}</p>
              </div>
            </div>
          </div>

          <div class="case-section">
            <h3>5.3 量化成果</h3>
            <div class="results-grid">
              <div class="result-card" v-for="r in bankResults" :key="r.metric">
                <div class="result-value" :style="{ color: r.color }">{{ r.value }}</div>
                <div class="result-metric">{{ r.metric }}</div>
                <div class="result-detail">{{ r.detail }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第六章 -->
        <section id="section6" class="chapter">
          <h2>六、案例二：某头部能源集团</h2>

          <div class="case-banner" style="background: linear-gradient(135deg, #D97706, #92400E);">
            <div class="case-banner-info">
              <div class="case-banner-tag">能源行业 · 央企集团</div>
              <h3>百亿级采购合规的持续监控实践</h3>
              <p>覆盖 <strong>120+子公司</strong>，合规问题发现率提升 <strong>6倍</strong>，审计人力节省 <strong>35%</strong></p>
            </div>
          </div>

          <div class="case-section">
            <h3>6.1 背景与挑战</h3>
            <div class="challenge-list">
              <div class="challenge-item" v-for="(c, i) in energyChallenges" :key="i">
                <div class="challenge-marker" style="background: #D97706;">{{ i + 1 }}</div>
                <div class="challenge-content">
                  <h4>{{ c.title }}</h4>
                  <p>{{ c.desc }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="case-section">
            <h3>6.2 解决方案</h3>
            <div class="solution-steps">
              <div class="solution-step" v-for="(s, i) in energySolutions" :key="i" :style="{ borderLeftColor: s.color }">
                <div class="solution-step-title">{{ s.title }}</div>
                <p>{{ s.desc }}</p>
              </div>
            </div>
          </div>

          <div class="case-section">
            <h3>6.3 量化成果</h3>
            <div class="results-grid">
              <div class="result-card" v-for="r in energyResults" :key="r.metric">
                <div class="result-value" :style="{ color: r.color }">{{ r.value }}</div>
                <div class="result-metric">{{ r.metric }}</div>
                <div class="result-detail">{{ r.detail }}</div>
              </div>
            </div>
          </div>

          <div class="visual-block blue-gradient" style="margin-top:32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">🏆</div>
              <div>
                <h4>两个案例的共同启示</h4>
                <p>无论是银行还是能源集团，成功的共同路径都是：先聚焦最高风险的单一业务场景打造样板 → 再基于数据积累扩展规则库 → 最后形成覆盖全集团的持续监控体系。没有一步到位，只有螺旋迭代。</p>
              </div>
            </div>
          </div>
        </section>

        <!-- 第七章 -->
        <section id="section7" class="chapter">
          <h2>七、常见挑战与应对策略</h2>

          <div class="challenges-full">
            <div class="challenge-full-card" v-for="ch in fullChallenges" :key="ch.challenge">
              <div class="challenge-full-header" :style="{ background: ch.color }">
                <span class="ch-icon">{{ ch.icon }}</span>
                <h4>{{ ch.challenge }}</h4>
              </div>
              <div class="challenge-full-body">
                <div class="ch-problem">
                  <span class="ch-label problem">❌ 常见表现</span>
                  <p>{{ ch.problem }}</p>
                </div>
                <div class="ch-solution">
                  <span class="ch-label solution">✅ 应对策略</span>
                  <p>{{ ch.solution }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="visual-block blue-gradient" style="margin-top:32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">📝</div>
              <div>
                <h4>结语：持续审计是一场组织变革</h4>
                <p>持续审计不仅是技术工具的升级，更是审计理念、组织能力、数据基础的系统性重构。它要求审计人员从"检查员"转型为"风险顾问"，要求IT团队提供强大的数据底座，要求管理层给予持续性的资源投入。古杉数据已协助多家央企和金融机构完成持续审计体系建设，欢迎交流共建。</p>
              </div>
            </div>
          </div>
        </section>

      </div>

      <!-- CTA -->
      <section class="cta-section">
        <h2>启动持续审计转型？</h2>
        <p>古杉数据提供从审计数字化规划、数据底座建设到持续审计平台落地的端到端服务</p>
        <div class="cta-buttons">
          <router-link to="/appointment" class="btn-primary">预约审计转型专家</router-link>
          <router-link to="/consulting/audit-risk-consulting" class="btn-outline">了解审计风控咨询</router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
export default {
  name: 'ContinuousAudit',
  data () {
    return {
      compareRows: [
        { dim: '审计频率', old: '每年1次', newVal: '每日/每周自动运行' },
        { dim: '覆盖范围', old: '抽样5%-15%', newVal: '全量数据100%覆盖' },
        { dim: '发现时效', old: '问题发生后数月才发现', newVal: '问题发生后24-72小时预警' },
        { dim: '人力投入', old: '大量人工核查时间', newVal: '自动化扫描，人工聚焦分析' },
        { dim: '证据留存', old: '纸质/分散文件', newVal: '数字化底稿，全程可追溯' },
        { dim: '风险应对', old: '事后纠正', newVal: '事中阻断，事前预警' }
      ],
      archLayers: [
        {
          name: '第一层：数据采集层',
          icon: '🗄️', bg: '#F0F7FF', titleColor: '#1A73E8',
          items: [
            { name: 'ERP/财务系统对接', desc: '实时或准实时抽取交易流水、记账凭证、预算执行数据' },
            { name: '业务系统集成', desc: '采购、合同、HR、OA等系统通过API或CDC方式接入' },
            { name: '外部数据接入', desc: '工商信息、法院公告、新闻舆情等第三方数据补充' }
          ]
        },
        {
          name: '第二层：分析引擎层',
          icon: '⚙️', bg: '#F0FFF4', titleColor: '#10B981',
          items: [
            { name: '规则引擎', desc: '500+内置审计规则，支持自定义扩展，覆盖财务/采购/合规场景' },
            { name: 'AI异常检测', desc: '机器学习模型自动识别非规则可定义的异常模式' },
            { name: '关联分析', desc: '图计算引擎分析关联方、资金流向、供应商关系网络' }
          ]
        },
        {
          name: '第三层：审计管理层',
          icon: '📊', bg: '#FFFBEB', titleColor: '#D97706',
          items: [
            { name: '预警工单管理', desc: '异常自动生成工单，分配给责任审计师，跟踪处理进度' },
            { name: '持续审计看板', desc: '实时展示覆盖率、预警数量、风险评分等核心指标' },
            { name: '报告自动生成', desc: '定期自动输出持续审计简报，支持多层级报送' }
          ]
        }
      ],
      techCapabilities: [
        { icon: '🔗', name: '数据集成能力', desc: '覆盖主流ERP（SAP/用友/金蝶）的预置连接器，支持增量同步', tools: ['SAP连接器', 'CDC变更捕获', 'API网关'] },
        { icon: '⚡', name: '实时流处理', desc: '对高频交易数据进行毫秒级流式处理，支持实时规则触发', tools: ['Kafka', 'Flink', 'Stream Processing'] },
        { icon: '🤖', name: 'AI异常检测', desc: '无监督学习识别历史数据中的异常基线，发现未知风险模式', tools: ['孤立森林', 'LSTM时序', '图神经网络'] },
        { icon: '📋', name: '规则管理平台', desc: '图形化规则配置界面，审计师无需编程即可维护规则库', tools: ['低代码规则引擎', '版本管理', '效果回测'] }
      ],
      implPhases: [
        {
          title: '诊断与规划',
          duration: '1-2个月',
          color: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
          goal: '摸清现状，确定持续审计的切入场景和优先级',
          tasks: [
            { name: '现状评估', desc: '梳理现有数据基础、系统架构、审计人员能力，评估持续审计就绪度' },
            { name: '场景优先级排序', desc: '按风险重要性 × 数据可得性 × 实施难度，筛选首批落地场景（如采购合规、费用报销）' },
            { name: '技术方案设计', desc: '确定数据采集方案、规则引擎选型、与现有系统的集成架构' }
          ],
          deliverable: '持续审计就绪度评估报告 + 实施路线图'
        },
        {
          title: 'MVP快速验证',
          duration: '2-3个月',
          color: 'linear-gradient(135deg, #10B981, #059669)',
          goal: '选择1-2个场景快速上线，验证技术可行性和业务价值',
          tasks: [
            { name: '数据管道搭建', desc: '建立目标系统到持续审计平台的数据采集链路，确保数据质量' },
            { name: '核心规则开发', desc: '开发首批50-100条高优先级审计规则，完成测试和阈值调优' },
            { name: '试运行与反馈', desc: '审计团队试用4-6周，收集规则准确率、误报率等反馈' }
          ],
          deliverable: 'MVP系统 + 规则效果评估报告 + 优化方案'
        },
        {
          title: '规模化推广',
          duration: '3-6个月',
          color: 'linear-gradient(135deg, #F59E0B, #D97706)',
          goal: '在MVP验证的基础上，扩展场景覆盖和组织范围',
          tasks: [
            { name: '规则库扩展', desc: '将规则从50条扩展至300+条，覆盖财务、采购、合同、费用等全业务域' },
            { name: '全量数据接入', desc: '逐步完成所有核心业务系统的数据接入，实现全量覆盖' },
            { name: '培训与推广', desc: '对审计团队开展持续审计操作培训，建立日常运营SOP' }
          ],
          deliverable: '生产环境完整系统 + 操作手册 + 培训材料'
        },
        {
          title: '持续优化运营',
          duration: '长期持续',
          color: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
          goal: '建立自我迭代的持续审计运营体系，持续提升覆盖质量',
          tasks: [
            { name: '规则持续优化', desc: '基于误报/漏报分析定期调整规则，引入新业务场景规则' },
            { name: 'AI模型迭代', desc: '利用历史审计发现数据持续训练AI模型，提升异常检测准确率' },
            { name: '效果评估报告', desc: '季度输出持续审计成效报告，向管理层展示价值' }
          ],
          deliverable: '季度效果报告 + 规则库更新记录 + 模型版本管理'
        }
      ],
      orgRoles: [
        { title: '持续审计负责人', color: '#0E1F52', resp: '统筹持续审计体系建设，向审计委员会汇报，协调IT与业务资源', skill: '审计专业背景 + 数字化转型经验 + 项目管理能力' },
        { title: '数据审计工程师', color: '#1A73E8', resp: '负责数据管道搭建、规则开发、技术平台运维', skill: 'SQL/Python + 数据工程 + ETL经验，了解审计业务逻辑' },
        { title: '业务审计专家', color: '#10B981', resp: '提供审计规则内容，负责预警工单核查处理，输出审计结论', skill: '财务/审计专业资质 + 业务系统熟悉度 + 分析判断能力' },
        { title: 'IT支持团队', color: '#F59E0B', resp: '提供系统对接支持，确保数据采集的稳定性和安全性', skill: '企业信息系统运维 + API集成经验 + 信息安全意识' }
      ],
      opsCycle: [
        { icon: '🔄', title: '自动扫描', desc: '持续审计引擎按预设频率（每日/每周）自动对全量数据执行规则检测', freq: '每日/每周', color: '#4A90D9' },
        { icon: '🔔', title: '预警分发', desc: '规则触发后自动生成工单，按业务域分配给对应审计师', freq: '实时触发', color: '#1F6FEB' },
        { icon: '🔍', title: '核查处理', desc: '审计师在SLA时限内完成核查，判定风险等级，形成审计发现', freq: '工作日24-48h', color: '#0E4EAD' },
        { icon: '📊', title: '简报汇总', desc: '系统自动汇总预警数量、处理率、风险分布，生成管理层简报', freq: '每周/每月', color: '#0A3A82' },
        { icon: '♻️', title: '规则迭代', desc: '基于误报/漏报统计定期优化规则，引入新的风险模式', freq: '每季度', color: '#0E1F52' }
      ],
      ksfList: [
        { icon: '🏛️', factor: '高层支持', desc: '持续审计需要穿越部门边界获取数据，没有管理层授权几乎无法推进' },
        { icon: '🗄️', factor: '数据基础扎实', desc: '数据质量低和接口不通是最常见的落地阻碍，需提前夯实数据治理底座' },
        { icon: '👥', factor: '审计与IT协同', desc: '规则开发需要审计懂业务、IT懂数据，两者缺一不可' },
        { icon: '🎯', factor: '小步快跑', desc: '切忌贪大求全，先在一个场景跑通ROI，再推广复制' }
      ],
      bankChallenges: [
        { title: '覆盖率严重不足', desc: '全行年度审计仅覆盖约15%的业务，大量高风险区域长期处于监控盲区，合规暴露面巨大' },
        { title: '问题发现严重滞后', desc: '问题从发生到被审计发现平均滞后8-10个月，期间损失持续扩大，止损能力极弱' },
        { title: '数据孤岛严重', desc: '核心银行系统、信贷系统、理财系统之间数据割裂，无法跨系统关联分析异常资金流' },
        { title: '审计人力严重不足', desc: '全行内审人员120人，监管要求覆盖范围却逐年扩大，人力缺口无法通过传统方式填补' }
      ],
      bankSolutions: [
        { title: '构建审计数据中台', desc: '整合核心银行、信贷、理财、支付等12个系统的数据，建立统一审计数据仓库，确保日增量数据T+1入仓。', color: '#4A90D9' },
        { title: '开发300+持续审计规则', desc: '覆盖资产业务、负债业务、中间业务、内控合规四大领域，规则经专家评审和历史数据回测后上线。', color: '#1F6FEB' },
        { title: '部署AI异常检测模型', desc: '利用2年历史交易数据训练孤立森林和LSTM模型，自动识别无规则覆盖的新型异常模式。', color: '#0E4EAD' },
        { title: '建立预警工单闭环', desc: '异常自动推送给业务条线审计专员，72小时内完成核查，审计结论数字化存档并纳入年度审计评价。', color: '#0E1F52' }
      ],
      bankResults: [
        { metric: '审计覆盖率', value: '15% → 78%', detail: '提升5倍以上，实现对全行主要业务场景的持续覆盖', color: '#1A73E8' },
        { metric: '问题发现时效', value: '8个月 → 72小时', detail: '发现周期压缩99%，从源头控制风险损失', color: '#10B981' },
        { metric: '年度节省人力', value: '约35人年', detail: '日常核查自动化，审计师专注高价值分析工作', color: '#F59E0B' },
        { metric: '合规问题发现量', value: '增加4倍', detail: '发现问题数量显著上升，同时误报率控制在8%以内', color: '#8B5CF6' }
      ],
      energyChallenges: [
        { title: '子公司分散，监管盲区多', desc: '120+子公司分布全国，总部审计资源严重不足，大多数子公司3年内未受到过实质性审计' },
        { title: '百亿采购合规风险高', desc: '集团年度采购规模超200亿元，供应商关联关系复杂，价格公允性难以核查，围标串标问题突出' },
        { title: '合同履约监控缺失', desc: '合同签订后缺乏系统性的履约追踪，尾款拖付、工程量虚报、竣工验收造假问题难以发现' }
      ],
      energySolutions: [
        { title: '构建集团统一采购审计数据平台', desc: '对接集中采购系统、ERP、合同管理系统，整合供应商工商数据，建立全量采购数据底座。', color: '#D97706' },
        { title: '开发采购合规专项规则库', desc: '针对围标串标、关联交易、价格虚高、质次价高等风险，开发180条专项规则，结合图计算分析供应商关系网络。', color: '#B45309' },
        { title: '推行子公司分级管控机制', desc: '按风险评分将子公司分为高/中/低三档，高风险子公司每周扫描，中风险每月，低风险每季度。', color: '#92400E' }
      ],
      energyResults: [
        { metric: '子公司审计覆盖率', value: '18% → 91%', detail: '从人工抽查扩展至全量持续监控', color: '#1A73E8' },
        { metric: '合规问题发现率', value: '提升6倍', detail: '年度发现合规问题从31件增至186件', color: '#10B981' },
        { metric: '审计人力节省', value: '35%', detail: '子公司日常核查自动化，节省约28人年', color: '#F59E0B' },
        { metric: '采购节省', value: '超1.2亿元', detail: '通过发现虚高定价和围标行为，直接降低采购成本', color: '#DC2626' }
      ],
      fullChallenges: [
        {
          icon: '🗄️', challenge: '数据底座不足',
          color: 'linear-gradient(135deg, #DC2626, #991B1B)',
          problem: '业务系统数据质量差、接口未开放、实时采集能力缺失，导致持续审计"无米之炊"',
          solution: '提前1年开展数据治理专项，重点解决核心系统数据接口、数据质量和数据字典三个问题，数据就绪度达到70%以上再启动持续审计'
        },
        {
          icon: '🚨', challenge: '规则误报率过高',
          color: 'linear-gradient(135deg, #EA580C, #9A3412)',
          problem: '规则设计过于激进，大量正常业务被标记为异常，审计师疲于应付误报，失去对系统的信任',
          solution: '上线前充分回测历史数据，初始阈值设置偏宽松，运行3个月后根据实际误报数据收紧；引入AI辅助分级，先让审计师处理高置信度预警'
        },
        {
          icon: '🤝', challenge: '业务部门配合度低',
          color: 'linear-gradient(135deg, #CA8A04, #854D0E)',
          problem: '持续监控被业务部门视为"被监视"，数据接口迟迟不配合开放，规则上线受阻',
          solution: '将持续审计定位为"帮助业务提前发现风险"而非"查错抓人"，早期邀请业务部门参与规则设计；审计发现优先通过内部沟通解决'
        },
        {
          icon: '👩‍💼', challenge: '审计人员技能不足',
          color: 'linear-gradient(135deg, #2563EB, #1E3A8A)',
          problem: '传统审计师不熟悉SQL、数据分析工具，无法高效处理系统生成的数字化预警',
          solution: '开展"审计+数据"融合培训，提供低代码规则配置工具降低技术门槛；配备1-2名数据审计工程师支撑团队'
        }
      ]
    }
  }
}
</script>

<style scoped>
.article-page { background: #fff; min-height: 100vh; }

.breadcrumb-nav { max-width: 960px; margin: 0 auto; padding: 20px 24px; font-size: 14px; color: #5A5E66; }
.breadcrumb-nav a { color: #1A73E8; text-decoration: none; }
.breadcrumb-nav a:hover { text-decoration: underline; }
.breadcrumb-nav .separator { margin: 0 8px; color: #ccc; }
.breadcrumb-nav .current { color: #0E1F52; }

.container { max-width: 960px; margin: 0 auto; padding: 0 24px 60px; }

.article-header { text-align: center; padding: 40px 0 32px; border-bottom: 1px solid #E8ECF0; margin-bottom: 40px; }
.article-header .article-tags { display: flex; justify-content: center; gap: 8px; margin-bottom: 20px; }
.article-header .tag { background: #EBF3FE; color: #1A73E8; padding: 4px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; }
.article-title { font-size: 2.2rem; font-weight: 800; color: #0E1F52; line-height: 1.3; margin-bottom: 16px; }
.article-subtitle { font-size: 1.1rem; color: #5A5E66; margin-bottom: 20px; }
.article-meta { display: flex; justify-content: center; gap: 24px; font-size: 14px; color: #8C8C8C; }

.intro-card { display: flex; gap: 16px; background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-left: 4px solid #1A73E8; border-radius: 12px; padding: 24px; margin-bottom: 40px; }
.intro-card .intro-icon { font-size: 2rem; flex-shrink: 0; }
.intro-card h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.intro-card p { font-size: 15px; color: #3D4150; line-height: 1.7; }

.toc { background: #F6F8FC; border-radius: 12px; padding: 24px 28px; margin-bottom: 48px; }
.toc h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.toc ul { list-style: none; padding: 0; }
.toc li { margin-bottom: 8px; }
.toc a { color: #1A73E8; text-decoration: none; font-size: 15px; }
.toc a:hover { text-decoration: underline; }

.article-body { font-size: 16px; line-height: 1.85; color: #3D4150; }
.article-body p { margin-bottom: 18px; }
.chapter { margin-bottom: 56px; }
.chapter h2 { font-size: 1.6rem; font-weight: 800; color: #0E1F52; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 3px solid #1A73E8; display: inline-block; }
.chapter h3 { font-size: 1.3rem; font-weight: 700; color: #0E1F52; margin: 28px 0 16px; }

.visual-block { border-radius: 12px; padding: 24px; margin: 24px 0; }
.blue-gradient { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; }
.visual-block-inner { display: flex; gap: 16px; align-items: center; }
.visual-icon { font-size: 2.5rem; flex-shrink: 0; }
.visual-block h4 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.visual-block p { font-size: 15px; line-height: 1.6; opacity: 0.9; margin: 0; }

.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 28px 0; }
.stat-card { background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-radius: 12px; padding: 24px; text-align: center; border: 1px solid #D6E4F0; }
.stat-number { font-size: 1.8rem; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.stat-label { font-size: 13px; color: #5A5E66; line-height: 1.5; }

.highlight-box { display: flex; gap: 16px; background: #FFF7E6; border-left: 4px solid #FAAD14; border-radius: 12px; padding: 24px; margin: 28px 0; }
.highlight-icon { font-size: 2rem; flex-shrink: 0; }
.highlight-box h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.highlight-box p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 对比表格 */
.compare-table-wrap { overflow-x: auto; margin: 24px 0; }
.compare-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.compare-table th { padding: 12px 16px; text-align: left; font-weight: 600; }
.compare-table th:first-child { background: #0E1F52; color: #fff; }
.compare-table th:nth-child(2) { background: #5A5E66; color: #fff; }
.compare-table th:nth-child(3) { background: #1A73E8; color: #fff; }
.compare-table td { padding: 12px 16px; border-bottom: 1px solid #E8ECF0; vertical-align: top; }
.compare-table tr:nth-child(even) td { background: #F6F8FC; }
.dim-col { font-weight: 600; color: #0E1F52; }
.old-col { color: #5A5E66; }
.new-col { color: #1A73E8; font-weight: 500; }

/* 技术架构 */
.arch-layers { margin: 24px 0; }
.arch-layer { border-radius: 12px; padding: 20px 24px; margin-bottom: 12px; border: 1px solid #E8ECF0; }
.arch-layer-title { font-size: 16px; font-weight: 700; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
.arch-icon { font-size: 1.4rem; }
.arch-layer-items { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.arch-item { background: rgba(255,255,255,.7); border-radius: 8px; padding: 12px; border: 1px solid #E8ECF0; }
.arch-item-name { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.arch-item-desc { font-size: 12px; color: #5A5E66; line-height: 1.5; }

/* 技术能力 */
.tech-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.tech-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.tech-icon { font-size: 2rem; margin-bottom: 8px; }
.tech-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.tech-card p { font-size: 13px; color: #5A5E66; margin-bottom: 10px; line-height: 1.5; }
.tech-tools { display: flex; flex-wrap: wrap; gap: 4px; }
.tech-tools span { background: #EBF3FE; color: #1A73E8; padding: 2px 8px; border-radius: 4px; font-size: 11px; }

/* 实施阶段 */
.impl-phases { margin: 24px 0; }
.impl-phase { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; margin-bottom: 16px; }
.impl-phase-header { display: flex; align-items: center; gap: 12px; padding: 14px 20px; color: #fff; }
.phase-num { font-size: 12px; opacity: 0.8; flex-shrink: 0; }
.phase-title { font-size: 16px; font-weight: 700; flex: 1; }
.phase-duration { font-size: 12px; background: rgba(255,255,255,.2); padding: 3px 10px; border-radius: 6px; flex-shrink: 0; }
.impl-phase-body { padding: 20px; background: #F6F8FC; }
.phase-goal { font-size: 14px; color: #3D4150; margin-bottom: 14px; }
.phase-tasks { margin-bottom: 12px; }
.phase-task { display: flex; gap: 10px; margin-bottom: 10px; }
.task-num { width: 22px; height: 22px; background: #0E1F52; color: #fff; border-radius: 50%; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
.task-name { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 2px; }
.task-desc { font-size: 13px; color: #5A5E66; line-height: 1.5; }
.phase-deliverable { font-size: 13px; color: #1A73E8; font-weight: 500; border-top: 1px solid #E8ECF0; padding-top: 10px; }

/* 组织角色 */
.org-roles { margin: 24px 0; }
.org-role { display: flex; gap: 16px; align-items: flex-start; background: #F6F8FC; border-radius: 12px; padding: 16px 20px; margin-bottom: 10px; border: 1px solid #E8ECF0; }
.role-badge { color: #fff; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 700; flex-shrink: 0; min-width: 120px; text-align: center; }
.role-info { flex: 1; }
.role-resp { font-size: 14px; color: #3D4150; margin-bottom: 4px; }
.role-skill { font-size: 13px; color: #5A5E66; margin: 0; }

/* 运营周期 */
.ops-cycle { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin: 24px 0; }
.ops-step { background: #F6F8FC; border-radius: 12px; padding: 16px; border: 1px solid #E8ECF0; text-align: center; }
.ops-marker { width: 44px; height: 44px; border-radius: 50%; margin: 0 auto 10px; display: flex; align-items: center; justify-content: center; font-size: 1.3rem; color: #fff; }
.ops-step h4 { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.ops-step p { font-size: 12px; color: #5A5E66; line-height: 1.5; margin-bottom: 8px; }
.ops-freq { font-size: 12px; color: #1A73E8; font-weight: 600; }

/* KSF */
.ksf-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.ksf-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.ksf-icon { font-size: 2rem; margin-bottom: 8px; }
.ksf-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.ksf-card p { font-size: 13px; color: #5A5E66; margin: 0; line-height: 1.5; }

/* 案例 Banner */
.case-banner { border-radius: 12px; padding: 28px 32px; margin-bottom: 28px; color: #fff; }
.case-banner-tag { font-size: 12px; background: rgba(255,255,255,.2); display: inline-block; padding: 3px 12px; border-radius: 20px; margin-bottom: 10px; }
.case-banner h3 { font-size: 1.4rem; font-weight: 800; margin-bottom: 8px; }
.case-banner p { font-size: 15px; opacity: 0.9; margin: 0; }
.case-banner strong { font-weight: 900; }

.case-section { margin-bottom: 28px; }

/* 挑战列表 */
.challenge-list { position: relative; padding-left: 40px; margin: 16px 0; }
.challenge-list::before { content: ''; position: absolute; left: 19px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #1A73E8, #0E1F52); border-radius: 2px; }
.challenge-item { position: relative; margin-bottom: 14px; }
.challenge-marker { position: absolute; left: -40px; width: 38px; height: 38px; border-radius: 50%; background: #1A73E8; color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; z-index: 1; }
.challenge-content { background: #F6F8FC; border-radius: 10px; padding: 14px 18px; border: 1px solid #E8ECF0; }
.challenge-content h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.challenge-content p { font-size: 13px; color: #5A5E66; margin: 0; line-height: 1.5; }

/* 解决方案步骤 */
.solution-steps { margin: 16px 0; }
.solution-step { border-left: 4px solid; padding: 14px 18px; background: #F6F8FC; border-radius: 0 10px 10px 0; margin-bottom: 10px; }
.solution-step-title { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.solution-step p { font-size: 13px; color: #5A5E66; margin: 0; line-height: 1.5; }

/* 成果 */
.results-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 16px 0; }
.result-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; text-align: center; }
.result-value { font-size: 1.3rem; font-weight: 900; margin-bottom: 6px; }
.result-metric { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.result-detail { font-size: 12px; color: #5A5E66; line-height: 1.5; }

/* 挑战全文 */
.challenges-full { margin: 24px 0; }
.challenge-full-card { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; margin-bottom: 16px; }
.challenge-full-header { display: flex; align-items: center; gap: 10px; padding: 14px 20px; color: #fff; }
.ch-icon { font-size: 1.4rem; }
.challenge-full-header h4 { font-size: 16px; font-weight: 700; margin: 0; }
.challenge-full-body { padding: 16px 20px; display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

.ch-label { display: inline-block; font-size: 12px; font-weight: 700; padding: 2px 8px; border-radius: 4px; margin-bottom: 6px; }
.ch-label.problem { background: #FEF2F2; color: #DC2626; }
.ch-label.solution { background: #F0FDF4; color: #16A34A; }
.ch-problem p, .ch-solution p { font-size: 14px; color: #3D4150; margin: 0; line-height: 1.6; }

/* CTA */
.cta-section { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); border-radius: 20px; padding: 48px; text-align: center; color: #fff; margin-top: 48px; }
.cta-section h2 { font-size: 1.8rem; font-weight: 800; margin-bottom: 12px; }
.cta-section p { font-size: 1rem; opacity: 0.85; margin-bottom: 28px; }
.cta-buttons { display: flex; justify-content: center; gap: 16px; }
.btn-primary { background: #fff; color: #0E1F52; padding: 14px 32px; border-radius: 10px; font-weight: 700; text-decoration: none; font-size: 15px; transition: all .2s; }
.btn-primary:hover { background: #EBF3FE; }
.btn-outline { border: 2px solid rgba(255,255,255,.6); color: #fff; padding: 14px 32px; border-radius: 10px; font-weight: 700; text-decoration: none; font-size: 15px; transition: all .2s; }
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,.1); }
</style>
